import { useContext, useMemo, useRef, useState } from "react";

import GenericBody from "../Componentes/GenericBody";
import NavBar from "../Componentes/NavBar";
import Overlay from "../Componentes/Overlay";
import SideMenu from "../Componentes/SideMenu";
import SideMenuBtn from "../Componentes/SideMenuBtn";
import AtestadosContent from "../Modulos/AtendimentoAmb/Atestados/Content";
import ModalNovoAtestado from "../Modulos/AtendimentoAmb/Atestados/ModalNovoAtestado";
import { AuthContext } from "../Auth/AuthProvider";
import { VerifyPermission } from "../Funcoes/VerifyPermission";
import { useModal } from "../contexts/ModalContext";
import { useToast } from "../contexts/ToastContext";
import useApi from "../hooks/useApi";

const PERMISSAO_BASE = { modulo: "ssma", submodulo: "atendimento-amb" };

const AtendimentoAmbAtestados = () => {
    const { openModal, closeModal, isModalOpen } = useModal();
    const { user } = useContext(AuthContext);
    const { notifyError } = useToast();
    const api = useApi();
    const contentRef = useRef(null);
    const [listRefreshKey, setListRefreshKey] = useState(0);
    const [loadingRelatorio, setLoadingRelatorio] = useState(false);

    const podeLancarEditar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "atestados-lancar-editar" }),
        [user]
    );

    const openSidebar = () => openModal("atestadosSidebar");

    const openNovoAtestado = () => {
        if (!podeLancarEditar) return;
        closeModal();
        openModal("atestadoNovo");
    };

    const gerarRelatorio = async () => {
        if (loadingRelatorio) return;

        const filtrados = contentRef.current?.getFilteredAtestados?.() ?? [];

        if (filtrados.length === 0) {
            notifyError("Nenhum atestado encontrado com os filtros atuais.");
            return;
        }

        setLoadingRelatorio(true);

        try {
            const response = await api.post(
                "/Painel/API/AtendimentoAmb/Atestados/GenerateRelatorio/index.php",
                filtrados,
                { responseType: "blob" }
            );

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "").slice(0, 14);
            link.href = url;
            link.setAttribute("download", `atestados_${stamp}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            let msg = "Erro ao gerar relatório.";
            if (err?.response?.data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await err.response.data.text());
                    if (parsed?.message) msg = parsed.message;
                } catch {
                    /* mantém mensagem padrão */
                }
            } else if (err?.response?.data?.message) {
                msg = err.response.data.message;
            }
            notifyError(msg);
        } finally {
            setLoadingRelatorio(false);
        }
    };

    return (
        <>
            <NavBar
                title="ATENDIMENTO AMBULATORIAL — ATESTADOS"
                goBackOnePage
                setSideBar={openSidebar}
            />
            <GenericBody>
                <AtestadosContent ref={contentRef} refreshKey={listRefreshKey} />
            </GenericBody>

            {isModalOpen("atestadosSidebar") && (
                <Overlay state changeContent={closeModal}>
                    <SideMenu>
                        {podeLancarEditar && (
                            <SideMenuBtn
                                button="NOVO ATESTADO"
                                clickFunc={openNovoAtestado}
                            />
                        )}
                        <SideMenuBtn
                            button="GERAR RELATÓRIO"
                            clickFunc={gerarRelatorio}
                            isLoading={loadingRelatorio}
                        />
                    </SideMenu>
                </Overlay>
            )}

            {podeLancarEditar && isModalOpen("atestadoNovo") && (
                <ModalNovoAtestado
                    fechar={closeModal}
                    onSaved={() => setListRefreshKey((k) => k + 1)}
                />
            )}
        </>
    );
};

export default AtendimentoAmbAtestados;
